import React from "react";

// Local calendar date, not toISOString(): the backend reads these as IST dates,
// and UTC would roll an IST user back a day before 05:30 — invisible on a wide
// window, but it would drop today's rows from a one-day window.
export function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

// Empty bounds mean "no date filter at all". Callers pass these through as
// `start_date: start || null`, so the parameter is simply omitted from the request
// — which keeps future-dated rows (e.g. scheduled payments) in range, as an
// end-of-today bound would not.
export const ALL_TIME = { start: "", end: "" };

export function isAllTime(range) {
  return !range.start && !range.end;
}

// Each dated range is inclusive of today, so "Last 7 days" spans today and the 6 before it.
export const PRESETS = [
  { label: "Today", days: 0 },
  { label: "Last 7 days", days: 6 },
  { label: "Last 30 days", days: 29 },
  { label: "All", all: true },
];

export function presetRange(days) {
  return { start: isoDate(daysAgo(days)), end: isoDate(new Date()) };
}

function rangeFor(preset) {
  return preset.all ? ALL_TIME : presetRange(preset.days);
}

export function formatWindow(start, end) {
  if (!start && !end) return "All time";
  const fmt = (iso) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  if (!start) return `up to ${fmt(end)}`;
  if (!end) return `from ${fmt(start)}`;
  return start === end ? fmt(start) : `${fmt(start)} – ${fmt(end)}`;
}

/**
 * Preset pills plus a start/end date pair, as a fragment so the caller's own
 * filter row lays it out alongside whatever else it holds.
 */
export default function DateRangeFilter({ value, onChange, showWindowLabel = false }) {
  const { start, end } = value;
  const activePreset = PRESETS.findIndex((p) => {
    const r = rangeFor(p);
    return r.start === start && r.end === end;
  });

  return (
    <>
      <div style={f.presets}>
        {PRESETS.map((p, i) => (
          <button
            key={p.label}
            onClick={() => onChange(rangeFor(p))}
            style={{
              ...f.preset,
              backgroundColor: activePreset === i ? "#eef2ff" : "#f1f5f9",
              color: activePreset === i ? "#4338ca" : "#64748b",
              border: activePreset === i ? "1.5px solid #4338ca" : "1.5px solid transparent",
              fontWeight: activePreset === i ? 700 : 500,
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div style={f.dateRange}>
        <input
          type="date"
          value={start}
          max={end}
          onChange={(e) => onChange({ ...value, start: e.target.value })}
          style={f.dateInput}
          title="Start date"
        />
        <span style={f.dateSep}>–</span>
        <input
          type="date"
          value={end}
          min={start}
          onChange={(e) => onChange({ ...value, end: e.target.value })}
          style={f.dateInput}
          title="End date"
        />
      </div>
      {showWindowLabel && !(start && end && start > end) && (
        <span style={f.windowLabel}>
          {formatWindow(start, end)}{start || end ? " · IST" : ""}
        </span>
      )}
    </>
  );
}

const f = {
  presets: { display: "flex", gap: 6, flexWrap: "wrap" },
  preset: { padding: "6px 14px", borderRadius: 20, fontSize: 12.5, cursor: "pointer", background: "none", transition: "all 0.15s" },
  dateRange: { display: "flex", alignItems: "center", gap: 4 },
  dateInput: { padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#1e293b", backgroundColor: "#fff" },
  dateSep: { color: "#94a3b8", fontSize: 13 },
  windowLabel: { fontSize: 12, color: "#94a3b8" },
};
